/**
 * @file Reminder detail modal owner for the Shared Calendar domain.
 */

import {
    doc,
    getDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { appData, state } from "../../app/state/state.js";
import {
    closeForm,
    configureDefaultFormActions,
    formModalElement,
} from "../../shared/forms/modalShell.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    escapeHTML,
    formatAndSanitizeUserContent,
} from "../../shared/utils/contentUtils.js";
import {
    deleteCalendarReminder,
    stopCalendarReminderRecurrence,
} from "./calendarApi.js";
import {
    formatCalendarDisplayDate,
} from "./calendarDisplayFormatting.js";
import {
    removeCalendarEntry,
} from "./calendarOptimisticEntries.js";
import { openReminderFormModal } from "./reminderFormModal.js";

function getCurrentUserId() {
    return state.currentUser?.uid || state.currentUserProfile?.id || "";
}

function getUserName(userId) {
    const user = (appData.users || []).find((entry) => entry.id === userId);
    return user?.name || user?.email || userId;
}

function formatUserList(ids = []) {
    const values = Array.isArray(ids) ? ids : [];
    return values.length ?
        values.map(getUserName).join(", ") :
        "None";
}

function formatDateText(reminderData) {
    return formatCalendarDisplayDate(
        reminderData?.reminderDate || reminderData?.startDate || "",
    );
}

function isRecurringReminder(reminderData) {
    const recurrence = reminderData?.recurrence || null;
    return Boolean(
        recurrence?.frequency &&
        recurrence.frequency !== "none" &&
        !recurrence.stoppedAt,
    );
}

function hasRecurrenceHistory(reminderData) {
    return Boolean(reminderData?.recurrence?.frequency);
}

function canEditReminder(reminderData) {
    if (state.currentUserProfile?.role === "admin") {return true;}
    return reminderData?.creatorId === getCurrentUserId();
}

function getTodayDateKey() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function isPastReminder(reminderData) {
    const reminderDate = isRecurringReminder(reminderData) ?
        reminderData?.nextOccurrenceDate || reminderData?.reminderDate || "" :
        reminderData?.reminderDate || reminderData?.startDate || "";
    return Boolean(reminderDate && reminderDate < getTodayDateKey());
}

async function fetchReminderData(reminderId) {
    if (!reminderId) {return null;}
    const snapshot = await getDoc(doc(db, "calendar_reminders", reminderId));
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

function getDetailRow(label, value) {
    return `
        <div>
            <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                ${escapeHTML(label)}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">${value}</dd>
        </div>
    `;
}

function formatThresholdValue(threshold) {
    const value = Number(threshold?.value);
    const unit = String(threshold?.unit || "");
    if (!Number.isInteger(value) || value <= 0 || !unit) {
        return "";
    }
    const unitLabel = value === 1 ? unit.replace(/s$/, "") : unit;
    return `${value} ${unitLabel} before`;
}

function formatReminderThresholds(reminderData) {
    const thresholds = Array.isArray(reminderData?.notificationThresholds) ?
        reminderData.notificationThresholds :
        [];
    const labels = thresholds
        .filter((entry) => entry?.enabled !== false)
        .map(formatThresholdValue)
        .filter(Boolean);
    if (labels.length) {
        return labels.join(", ");
    }
    return "No upcoming reminder";
}

function formatRecurrenceSummary(recurrence) {
    if (!recurrence?.frequency || recurrence.frequency === "none") {
        return "Does not repeat";
    }
    if (recurrence.stoppedAt) {
        return "Stopped";
    }
    const interval = Number(recurrence.interval || 1);
    const unitByFrequency = {
        daily: "day",
        weekly: "week",
        monthly: "month",
        yearly: "year",
    };
    const unit = unitByFrequency[recurrence.frequency] || "occurrence";
    const cadence = interval <= 1 ?
        recurrence.frequency[0].toUpperCase() + recurrence.frequency.slice(1) :
        `Every ${interval} ${unit}s`;
    const end = recurrence.endDate ?
        ` until ${formatCalendarDisplayDate(recurrence.endDate)}` :
        "";
    return `${cadence}${end}`;
}

function formatOccurrenceDateTime(date, time = "") {
    const dateText = formatCalendarDisplayDate(date || "");
    return [dateText, time].filter(Boolean).join(" at ") || "None scheduled";
}

function getRecurrenceRows(data) {
    if (!hasRecurrenceHistory(data)) {return "";}
    const recurrence = data.recurrence || {};
    return `
        ${getDetailRow("Repeat", escapeHTML(formatRecurrenceSummary(recurrence)))}
        ${getDetailRow("Next occurrence", escapeHTML(formatOccurrenceDateTime(
            data.nextOccurrenceDate,
            data.nextOccurrenceTime,
        )))}
    `;
}

function renderReminderDetail(entry, reminderData) {
    const data = reminderData || entry || {};
    return `
        <div class="space-y-4" data-calendar-reminder-detail>
            <div>
                <h4 class="text-lg font-semibold text-gray-900">
                    ${escapeHTML(data.title || "Untitled reminder")}
                </h4>
                <p class="mt-1 text-sm text-gray-500">
                    ${escapeHTML(formatDateText(data))}
                </p>
            </div>
            <dl class="grid grid-cols-1 gap-4 md:grid-cols-2">
                ${getDetailRow("Owner", escapeHTML(getUserName(data.creatorId)))}
                ${getDetailRow("Assigned Users", escapeHTML(formatUserList(data.assignedUserIds)))}
                ${getDetailRow("Time", escapeHTML(data.reminderTime || "09:00"))}
                ${getDetailRow("Reminders", escapeHTML(formatReminderThresholds(data)))}
                ${getRecurrenceRows(data)}
            </dl>
            ${data.description ? `
                <div>
                    <h5 class="text-sm font-semibold text-gray-700">Description</h5>
                    <div class="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                        ${formatAndSanitizeUserContent(data.description)}
                    </div>
                </div>
            ` : ""}
        </div>
    `;
}

function createPreviewActionButton({ label, icon, variant = "brand", onClick }) {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("aria-label", label);
    button.title = label;
    button.dataset.modalCustomActions = "true";
    button.className = [
        "inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent",
        "text-gray-500 transition-colors focus:outline-none focus-visible:ring-2",
        variant === "danger" ?
            "hover:text-red-600 focus-visible:ring-red-200" :
            "hover:text-[var(--dfc-brand-primary)] focus-visible:ring-[var(--dfc-brand-primary-ring)]",
    ].join(" ");
    button.addEventListener("click", onClick);

    const iconElement = document.createElement("span");
    iconElement.className = "material-symbols-outlined text-[20px] leading-none";
    iconElement.setAttribute("aria-hidden", "true");
    iconElement.textContent = icon;
    button.append(iconElement);
    return button;
}

function configurePreviewActions({ reminderData, canEdit }) {
    const cancelBtn = document.getElementById("cancel-form-btn");
    const submitBtn = formModalElement?.querySelector("button[type=\"submit\"]");
    const footer = cancelBtn?.parentElement || submitBtn?.parentElement;
    const formHeaderActions = formModalElement?.querySelector("#form-header-actions");
    if (!cancelBtn || !submitBtn || !footer || !formHeaderActions) {return;}

    formHeaderActions.innerHTML = "";
    footer.style.display = "none";
    submitBtn.style.display = "none";
    cancelBtn.style.display = "none";

    if (canEdit && reminderData?.id) {
        if (isRecurringReminder(reminderData)) {
            const stopButton = createPreviewActionButton({
                label: "Stop Recurrence",
                icon: "event_busy",
                variant: "danger",
                onClick: () => openStopRecurrenceConfirmation(reminderData),
            });
            formHeaderActions.append(stopButton);
        }

        const deleteButton = createPreviewActionButton({
            label: hasRecurrenceHistory(reminderData) ?
                "Delete Reminder Series" :
                "Delete Reminder",
            icon: "delete",
            variant: "danger",
            onClick: () => openReminderDeleteConfirmation(reminderData),
        });
        formHeaderActions.append(deleteButton);

        if (!isPastReminder(reminderData)) {
            const editButton = createPreviewActionButton({
                label: "Edit Reminder",
                icon: "edit",
                onClick: () => {
                    closeForm();
                    openReminderFormModal(reminderData);
                },
            });
            formHeaderActions.append(editButton);
        }
    }

    const closeButton = createPreviewActionButton({
        label: "Close Reminder Details",
        icon: "close",
        variant: "danger",
        onClick: closeForm,
    });
    formHeaderActions.append(closeButton);
}

function openStopRecurrenceConfirmation(reminderData) {
    const reminderName = reminderData.title || "this reminder";
    openConfirmationModal(
        "Stop Recurrence",
        `
            <div class="space-y-3 text-left">
                <p class="text-sm text-gray-700">
                    Stop future occurrences and notifications for
                    <strong>${escapeHTML(reminderName)}</strong>?
                </p>
                <p class="text-sm text-gray-500">
                    Future calendar entries and pending notifications are
                    removed. The reminder record and completed occurrence
                    history stay in place. Use Delete Reminder Series only when
                    the whole series should be removed.
                </p>
            </div>
        `,
        async () => {
            await stopCalendarReminderRecurrence(
                reminderData.id,
                reminderData.updatedAt,
            );
            removeCalendarEntry("reminder", reminderData.id);
            closeForm();
        },
        true,
        "Stop Recurrence",
        null,
        "Stopping...",
        "Cancel",
        {
            actionsLayout: "right",
        },
    );
}

function openReminderDeleteConfirmation(reminderData) {
    const reminderName = reminderData.title || "this reminder";
    const isSeries = hasRecurrenceHistory(reminderData);
    const getNotifyUsers = () => document
        .querySelector("input[name=\"calendar-reminder-delete-notify\"]:checked")
        ?.value === "true";

    openConfirmationModal(
        isSeries ? "Delete Reminder Series" : "Delete Reminder",
        `
            <div class="space-y-4 text-left">
                <p class="text-sm text-gray-700">
                    Do you want to permanently delete
                    <strong>${escapeHTML(reminderName)}</strong>${isSeries ?
                        " and all future occurrences in this series" :
                        ""} and notify the owner and assigned users?
                </p>
                ${isSeries ? `
                    <p class="text-sm text-gray-500">
                        This removes the whole Reminder series. To keep the
                        reminder and only stop future recurrence, choose Stop
                        Recurrence instead.
                    </p>
                ` : ""}
                <fieldset class="space-y-2">
                    <legend class="sr-only">Notify affected users</legend>
                    <label class="settings-radio-choice">
                        <input
                            type="radio"
                            name="calendar-reminder-delete-notify"
                            value="true"
                            class="settings-radio-input sr-only"
                        >
                        <span class="settings-radio-marker" aria-hidden="true">
                            <span class="settings-radio-dot"></span>
                        </span>
                        <span>Notify users</span>
                    </label>
                    <label class="settings-radio-choice">
                        <input
                            type="radio"
                            name="calendar-reminder-delete-notify"
                            value="false"
                            class="settings-radio-input sr-only"
                            checked
                        >
                        <span class="settings-radio-marker" aria-hidden="true">
                            <span class="settings-radio-dot"></span>
                        </span>
                        <span>Don't notify users</span>
                    </label>
                </fieldset>
            </div>
        `,
        async () => {
            await deleteCalendarReminder(
                reminderData.id,
                reminderData.updatedAt,
                getNotifyUsers(),
            );
            removeCalendarEntry("reminder", reminderData.id);
            closeForm();
        },
        true,
        isSeries ? "Delete Series" : "Delete",
        null,
        "Deleting...",
        "Cancel",
        {
            actionsLayout: "right",
        },
    );
}

/**
 * Opens read-only Calendar reminder detail.
 * @param {object} entry Display-safe calendar entry.
 * @returns {void}
 */
export function openReminderDetailModal(entry) {
    if (!formModalElement) {
        console.error("Form modal element (#form-modal) not found.");
        return;
    }

    configureDefaultFormActions();
    const formTitle = formModalElement.querySelector("#form-title");
    const formHeaderActions = formModalElement.querySelector("#form-header-actions");
    const formFields = formModalElement.querySelector("#form-fields");
    const itemForm = formModalElement.querySelector("#item-form");
    if (!formTitle || !formHeaderActions || !formFields || !itemForm) {
        console.error("Required elements missing within #form-modal.");
        return;
    }

    formTitle.textContent = "Reminder Details";
    formHeaderActions.innerHTML = "";
    itemForm.onsubmit = (event) => event.preventDefault();
    formFields.innerHTML = renderReminderDetail(entry, entry);
    configurePreviewActions({ reminderData: null, canEdit: false });
    formModalElement.classList.add("flex");

    fetchReminderData(entry?.sourceId)
        .then((reminderData) => {
            if (!reminderData) {return;}
            formFields.innerHTML = renderReminderDetail(entry, reminderData);
            configurePreviewActions({
                reminderData,
                canEdit: canEditReminder(reminderData),
            });
        })
        .catch((error) => {
            console.error("Error loading Calendar reminder detail:", error);
        });
}
